import {AddIcon, useDisclose} from "@hosttools/frontend";
import {ReactComponent as Logo} from "@hosttools/frontend/assets/group-channels.svg";
import {
    Box,
    Button,
    ButtonIcon,
    ButtonText,
    Center,
    Steps,
    VStack
} from "@hosttools/frontend/components";
import {useMobileView} from "@hosttools/frontend/hooks/useMobileView";
import type {AccountType} from "@hosttools/frontend/models/account";
import type React from "react";
import {memo, useCallback} from "react";
import {useHistory} from "react-router-dom";

import ListingForm from "../ListingContent/ListingForm";

import ChannelItem from "./ChannelItem";

import Heading from "@/client/components/Heading";
import Paragraph from "@/client/components/Paragraph";

const importingChannels = ["Airbnb", "HomeAway", "Booking", "Houfy"] as const;

export type Props = {
    onPressChannel: (accountType: AccountType) => void;
    onAddListingManualSuccess: () => void;
};

const SelectChannels: React.FC<Props> = ({onPressChannel, onAddListingManualSuccess}) => {
    const history = useHistory();
    const {
        isOpen: addListingManual,
        onOpen: openAddListingManual,
        onClose: closeAddListingManual
    } = useDisclose();
    const isMobile = useMobileView();

    const handleDoThisLater = useCallback(() => {
        history.push("/");
    }, [history]);

    return (
        <Center className="w-full pt-10 px-5 md:px-24">
            {addListingManual ? (
                <VStack space="3xl">
                    <Heading variant="xl">Add listing</Heading>
                    <ListingForm
                        isSimple
                        listing={undefined}
                        onCancel={closeAddListingManual}
                        onSave={onAddListingManualSuccess}
                    />
                </VStack>
            ) : (
                <VStack className="max-w-[700px] w-full" space="5xl">
                    <VStack className="items-start md:items-center" space="lg">
                        <Box className="hidden md:block">
                            <Logo />
                        </Box>
                        <Steps className="flex md:hidden" step={1} totalSteps={3} />
                        <VStack className="gap-2 md:gap-4">
                            <Heading variant={isMobile ? "md" : "2xl"}>Connect channel</Heading>
                            <Paragraph variant={isMobile ? "sm" : "lg"}>
                                Import your listings from one of these channels or manually create a
                                listing on Host Tools
                            </Paragraph>
                        </VStack>
                    </VStack>
                    <VStack space="2xl">
                        <VStack space="lg">
                            {importingChannels.map(elem => {
                                return (
                                    <ChannelItem
                                        key={elem}
                                        accountType={elem}
                                        isDisabled={false}
                                        onPress={onPressChannel}
                                    />
                                );
                            })}
                        </VStack>
                        <VStack space="sm">
                            <Button
                                action="secondary"
                                className="w-full flex-shrink-0"
                                variant="outline"
                                onPress={openAddListingManual}
                            >
                                <ButtonIcon as={AddIcon} />
                                <ButtonText>Add manually</ButtonText>
                            </Button>
                            <Button
                                action="primary"
                                className="w-full flex-shrink-0"
                                variant="ghost"
                                onPress={handleDoThisLater}
                            >
                                <ButtonText>Do this later</ButtonText>
                            </Button>
                        </VStack>
                    </VStack>
                </VStack>
            )}
        </Center>
    );
};

export default memo(SelectChannels);
